import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Button,
  Modal,
  Input,
  Table,
  Menu,
  Dropdown,
} from "antd";
import { useHistory } from "react-router-dom";
import { RiEditFill, RiDeleteBin2Fill } from "react-icons/ri";
import {
  getAllMobileCopilotChatsPaginatedSearch,
  deleteMobileCopilotChatById,
} from "@api/copilots/mobile-app/mobileCopilotChat";
import {
  SuccessNotification,
  ErrorNotification,
  MobileCopilotChatCreateModal,
  MobileCopilotChatUpdateModal,
} from "@components";
import { IoAddSharp } from "react-icons/io5";

const { Search } = Input;

const MobileCopilotChatsList = () => {
  const history = useHistory();

  const [mobileCopilotChats, setMobileCopilotChats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [mobileCopilotChatId, setMobileCopilotChatId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const page = parseInt(searchParams.get("page") || "1", 10);
    const pageSize = parseInt(searchParams.get("pageSize") || "10", 10);
    const search = searchParams.get("search") || "";

    setSearchTerm(search);
    setPagination({ current: page, pageSize, total: 0 });
    fetchMobileCopilotChats(page, pageSize, search);
  }, []);

  const fetchMobileCopilotChats = async (
    page = pagination.current,
    pageSize = pagination.pageSize,
    search = searchTerm
  ) => {
    setLoading(true);
    try {
      const res = await getAllMobileCopilotChatsPaginatedSearch(
        page,
        pageSize,
        search
      );
      setMobileCopilotChats(res.data.mobileCopilotChats);
      setPagination((prev) => ({
        ...prev,
        total: res.data.paginationData.totalMobileCopilotChats,
        current: res.data.paginationData.page,
        pageSize: res.data.paginationData.perPage,
      }));
    } catch (error) {
      console.error("Fetch mobileCopilotChats error", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePaginationChange = (page, pageSize) => {
    const searchParams = new URLSearchParams();
    searchParams.set("page", page);
    searchParams.set("pageSize", pageSize);
    if (searchTerm) searchParams.set("search", searchTerm);

    history.push({ search: searchParams.toString() });
    setPagination({ ...pagination, current: page, pageSize });
    fetchMobileCopilotChats(page, pageSize, searchTerm);
  };

  const handleSearch = (searchValue) => {
    setSearchTerm(searchValue);
    setPagination({ ...pagination, current: 1 }); // Reset to first page
    fetchMobileCopilotChats(1, pagination.pageSize, searchValue);

    const searchParams = new URLSearchParams();
    searchParams.set("page", "1");
    searchParams.set("pageSize", pagination.pageSize);
    if (searchValue) searchParams.set("search", searchValue);
    history.push({ search: searchParams.toString() });
  };

  // Handle create mobileCopilotChat modal
  const handleCreateModalClick = () => {
    setCreateModalOpen(!createModalOpen);
  };

  // Handle update mobileCopilotChat modal
  const handleUpdateModalClick = (id) => {
    setMobileCopilotChatId(id);
    setUpdateModalOpen(!updateModalOpen);
  };

  // Handle mobileCopilotChat delete
  const handleMobileCopilotChatDelete = (id) => {
    // Confirm delete
    Modal.confirm({
      title: "Are you sure you want to delete this copilot chat?",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          await deleteMobileCopilotChatById(id);
          // Fetch all mobileCopilotChatChats
          fetchMobileCopilotChats();
          // Show success notification
          SuccessNotification("Copilot Chat deleted successfully");
        } catch (error) {
          // Show error notification
          ErrorNotification("Error deleting copilot chat");
        }
      },
    });
  };

  // Dropdown menu for additional actions
  const menu = (
    <Menu>
      {/* Browser Copilots Categories */}
      <Menu.Item
        key="1"
        onClick={() => {
          history.push("/super-admin/copilots/browser-extension/categories");
        }}
      >
        Browser Copilots Categories
      </Menu.Item>

      {/* Browser Copilots */}
      <Menu.Item
        key="2"
        onClick={() => {
          history.push("/super-admin/copilots/browser-extension/copilots");
        }}
      >
        Browser Copilots
      </Menu.Item>

      {/* Browser Copilot Chats */}
      <Menu.Item
        key="3"
        onClick={() => {
          history.push("/super-admin/copilots/browser-extension/copilot-chats");
        }}
      >
        Browser Copilot Chats
      </Menu.Item>

      {/* Mobile App Copilot Chats Categories */}
      <Menu.Item
        key="4"
        onClick={() => {
          history.push("/super-admin/copilots/mobile-app/categories");
        }}
      >
        Mobile App Copilot Chat Categories
      </Menu.Item>
    </Menu>
  );

  const columns = [
    {
      title: "Order",
      dataIndex: "order",
      key: "order",
      width: 100,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 200,
    },
    {
      title: "Model",
      dataIndex: "model",
      key: "model",
      width: 200,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 300,
      ellipsis: true,
    },
    {
      title: "Temperature",
      dataIndex: "temperature",
      key: "temperature",
      width: 150,
    },
    {
      title: "Max Tokens",
      dataIndex: "max_tokens",
      key: "max_tokens",
      width: 150,
    },
    {
      title: "Is Useful",
      dataIndex: "useful_generations",
      key: "useful_generations",
      width: 150,
      render: (useful_generations) => useful_generations?.length || 0,
    },
    {
      title: "Is Poor",
      dataIndex: "poor_generations",
      key: "poor_generations",
      width: 150,
      render: (poor_generations) => poor_generations?.length || 0,
    },
    {
      title: "Usage",
      dataIndex: "used",
      key: "used",
      width: 150,
    },
    {
      title: "Likes",
      dataIndex: "likes",
      key: "likes",
      width: 150,
      render: (likes) => likes?.length || 0,
    },
    {
      title: "Action",
      key: "action",
      width: 200,
      render: (_, record) => (
        <span>
          <RiEditFill
            style={{ marginRight: 12, cursor: "pointer" }}
            onClick={() => handleUpdateModalClick(record._id)}
          />
          <RiDeleteBin2Fill
            style={{ cursor: "pointer" }}
            onClick={() => handleMobileCopilotChatDelete(record._id)}
          />
        </span>
      ),
    },
  ];

  return (
    <>
      {/* Create MobileCopilotChat Modal */}
      {createModalOpen && (
        <MobileCopilotChatCreateModal
          open={createModalOpen}
          handleModalClick={handleCreateModalClick}
          fetchMobileCopilotChats={fetchMobileCopilotChats}
        />
      )}

      {/* Update MobileCopilotChat Modal */}
      {updateModalOpen && (
        <MobileCopilotChatUpdateModal
          open={updateModalOpen}
          handleModalClick={handleUpdateModalClick}
          fetchMobileCopilotChats={fetchMobileCopilotChats}
          mobileCopilotChatId={mobileCopilotChatId}
        />
      )}

      <Card style={{ borderRadius: "0", height: "100vh" }}>
        <Row>
          <Col span={24}>
            <Row justify="space-between">
              <Col span={8}>
                <h3 className="hp-mb-16">Mobile App - Copilot Chats List</h3>
              </Col>

              {/* Search Input */}
              <Col>
                <Search
                  placeholder="Search by copilot chat name"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onSearch={handleSearch}
                  style={{ width: "300px", marginRight: 16 }}
                />

                {/* Categories Button */}
                <Button
                  type="default"
                  style={{ marginRight: 16 }}
                  onClick={() =>
                    history.push("/super-admin/mobile-copilot-chat/categories")
                  }
                >
                  Categories
                </Button>

                {/* Create MobileCopilotChat Button */}
                <Button type="primary" onClick={handleCreateModalClick}>
                  Create Mobile Copilot Chat
                </Button>

                {/* More Options */}
                <Dropdown overlay={menu} placement="bottomRight">
                  <Button
                    icon={<IoAddSharp />}
                    style={{ marginLeft: "10px" }}
                  />
                </Dropdown>
              </Col>
            </Row>

            <Row>
              <Col span={24}>
                <Table
                  columns={columns}
                  dataSource={mobileCopilotChats}
                  pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                  }}
                  loading={loading}
                  onChange={({ current, pageSize }) =>
                    handlePaginationChange(current, pageSize)
                  }
                  rowKey={(record) => record._id}
                  scroll={{ y: "calc(100vh - 270px)", x: "max-content" }}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </Card>
    </>
  );
};

export default MobileCopilotChatsList;
